// Configuración de márgenes y tamaño de los tickets de impresión de
// factura/nota de crédito (Ecuador/Paraguay, Honduras, Perú). Mismo patrón
// que getSyncConfig/setSyncConfig (syncService.js): localStorage, merge con
// defaults al leer. No hay tabla Dexie de settings genérica en el proyecto --
// localStorage es el patrón establecido para preferencias de la app.
//
// A diferencia de syncConfig, el default no es un único objeto fijo: la
// primera vez que se abre la pestaña de Configuración (antes de guardar nada)
// debe reflejar lo que hoy ya está hardcodeado por país, para que el admin
// vea "los valores que ya tienen definidos" y no un punto de partida
// arbitrario. Una vez guardado, el config queda como un solo objeto (no
// separado por país) -- cada TPV opera un país a la vez.
const STORAGE_KEY = "ticketPrintConfig";

/**
 * Qué ticket se dibuja al pulsar el ojito o la impresora.
 *
 * - `default`: el que la app viene sacando hasta hoy para ese país.
 * - `personalizado`: el formato propio del país, cuando lo tiene. Hoy sólo
 *   Costa Rica, con el diseño de su sistema anterior.
 * - `ambos`: los dos, con un selector arriba para pasar de uno al otro. Sirve
 *   para compararlos contra el papel antes de decidir cuál queda.
 *
 * Un país sin formato propio se comporta como `default` aunque acá diga otra
 * cosa: el selector de la pantalla de Configuración lo avisa.
 */
/**
 * Con cuántos decimales se escribe un importe **en el papel**.
 *
 * Es aparte de `decimals` de countries.js a propósito. Costa Rica declara ahí
 * cinco --la precisión con la que calcula-- y el tiquete salía con
 * «23.998,56288» donde el papel de Santillana dice «23.998,56». Cambiarlo en el
 * catálogo del país habría movido todas las pantallas; acá sólo mueve el
 * tiquete, que es donde importa el papel.
 *
 * No toca el cálculo, que sigue viniendo de `resolveMoneyDecimals(country)`.
 */
export const FORMATOS_DE_TICKET = ["default", "personalizado", "ambos"];

const COUNTRY_DEFAULTS = {
  // Común (InvoiceTicket.js): hoy padding 20px en pantalla / 15px en
  // impresión y max-width 350px en CSS, popup de impresión fijo a 80mm.
  EC: {
    marginTop: 5,
    marginRight: 5,
    marginBottom: 5,
    marginLeft: 5,
    width: 80,
    fontScale: 1,
    printMode: "image",
    formato: "default",
    decimales: 2,
  },
  PY: {
    marginTop: 5,
    marginRight: 5,
    marginBottom: 5,
    marginLeft: 5,
    width: 80,
    fontScale: 1,
    printMode: "image",
    formato: "default",
    decimales: 0,
  },
  // Costa Rica aprovecha el ancho del rollo: el tiquete lleva seis columnas de
  // línea y descripciones largas, así que un margen de 5 mm por lado se comía
  // el sitio que necesita la última columna.
  CR: {
    marginTop: 1,
    marginRight: 1,
    marginBottom: 1,
    marginLeft: 1,
    width: 80,
    fontScale: 1,
    printMode: "image",
    // El diseño propio de Costa Rica, no el genérico: es el que lleva la clave
    // numérica, el consecutivo de Hacienda y los dos códigos de barras.
    formato: "personalizado",
    decimales: 2,
  },
  // Panamá lleva las mismas seis columnas de línea que Costa Rica, así que
  // necesita el mismo margen de 1 mm para que quepa la última.
  //
  // Arranca en `default` --el tiquete de siempre-- a propósito: el formato
  // propio es el CAFE de la DGI, y hasta que CertificaE emita la factura
  // electrónica ese papel sale con tres huecos (CUFE, autorización y QR). Se
  // elige desde Configuración cuando se quiera ver cómo va quedando.
  PA: {
    marginTop: 1,
    marginRight: 1,
    marginBottom: 1,
    marginLeft: 1,
    width: 80,
    fontScale: 1,
    printMode: "image",
    formato: "default",
    decimales: 2,
  },
  // Honduras/Perú: @page{margin:0}, contenedor sin padding propio, width 72mm.
  HN: {
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    width: 72,
    fontScale: 1,
    printMode: "image",
    formato: "default",
    decimales: 2,
  },
  PE: {
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    width: 72,
    fontScale: 1,
    printMode: "image",
    formato: "default",
    decimales: 2,
  },
};

const FALLBACK_DEFAULT = {
  marginTop: 5,
  marginRight: 5,
  marginBottom: 5,
  marginLeft: 5,
  width: 80,
  fontScale: 1,
  printMode: "image",
  formato: "default",
  decimales: 2,
};

export const getDefaultTicketPrintConfig = (countryCode) =>
  COUNTRY_DEFAULTS[countryCode] || FALLBACK_DEFAULT;

export const getTicketPrintConfig = (countryCode) => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const defaults = getDefaultTicketPrintConfig(countryCode);
    if (!stored) return defaults;
    return { ...defaults, ...JSON.parse(stored) };
  } catch (error) {
    console.error("Error al obtener configuración de impresión de tickets:", error);
    return getDefaultTicketPrintConfig(countryCode);
  }
};

export const setTicketPrintConfig = (config) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
};

/**
 * Estilo inline a aplicar sobre el contenedor del ticket (el nodo que se
 * captura con html2canvas) para que el ancho/márgenes/escala de fuente
 * configurados se reflejen tanto en pantalla como en la imagen capturada.
 * `zoom` se usa en vez de `transform: scale()` porque sí participa del
 * layout/flujo de caja (transform solo afecta el render visual, no el
 * tamaño real del nodo que html2canvas mide) -- confirmar visualmente que
 * no introduce recortes antes de dar por bueno.
 *
 * `boxSizing: border-box` no es un detalle: **el ancho configurado es el del
 * papel, y los márgenes van dentro**. Sin él, el proyecto no declara
 * border-box en ningún sitio, así que un ticket de 80mm con 5mm de margen a
 * cada lado medía 90mm de verdad y se salía del papel. Los dos modos de
 * impresión disimulaban ese desborde de forma distinta --la imagen se
 * encogía para caber y se veía centrada; el texto se pegaba a la izquierda y
 * se salía por la derecha-- y de ahí venía que el mismo ticket apareciera en
 * un sitio distinto según el modo.
 *
 * `maxWidth: none` acompaña por lo mismo: la hoja de estilos del ticket lo
 * limita a 350px (~93mm), y ese tope se comería en silencio cualquier ancho
 * de papel configurado por encima.
 *
 * @returns {import("react").CSSProperties}
 */
export const buildTicketContainerStyle = (config) => ({
  width: `${config.width}mm`,
  maxWidth: "none",
  boxSizing: "border-box",
  margin: "0 auto",
  paddingTop: `${config.marginTop}mm`,
  paddingRight: `${config.marginRight}mm`,
  paddingBottom: `${config.marginBottom}mm`,
  paddingLeft: `${config.marginLeft}mm`,
  zoom: config.fontScale,
});

/**
 * CSS del `@page`/`body` de la impresión, compartido por los dos modos.
 *
 * Es el mismo para la ventana emergente del modo imagen y para el iframe de
 * react-to-print, y ésa es la razón de que exista: cuando cada modo traía su
 * propio CSS, el mismo ticket salía centrado en uno y pegado a la izquierda en
 * el otro. El centrado va explícito --no heredado de un `margin: auto` que se
 * anula solo cuando el contenido desborda-- para que el resultado no dependa de
 * si el papel del preview es más ancho que el rollo.
 */
export const buildTicketPrintPageCss = (config) => `
  @page { size: ${config.width}mm auto; margin: 0; }
  html, body { width: ${config.width}mm; margin: 0 auto; padding: 0; }
  body { display: flex; justify-content: center; }
`;

/**
 * Opciones comunes de useReactToPrint (modo "html" -- texto real en vez de
 * imagen, para impresoras de matriz de puntos) compartidas por los tickets
 * de factura. Reusa el mismo CSS de `buildTicketPrintPageCss` que ya usa el
 * modo imagen, para que el ancho de papel/márgenes configurados sean
 * idénticos entre ambos modos. `contentRef` y `onAfterPrint` quedan fuera a
 * propósito: son por componente (cada ticket tiene su propio ref y su
 * propio onClose de modal).
 */
export const getReactToPrintOptions = (config, documentTitle) => ({
  documentTitle,
  pageStyle: buildTicketPrintPageCss(config),
});
